import React, { ReactNode } from 'react';
import {
  Box,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ArrowUpward as ArrowUpwardIcon, ArrowDownward as ArrowDownwardIcon, UnfoldMore as UnfoldMoreIcon } from '@mui/icons-material';
import { SxProps, Theme } from '@mui/material/styles';
import i18next from 'i18next';

// --- カラム定義 ---
export interface SortableTableColumn<T> {
  key: string;
  label: string;
  width?: string | number;
  minWidth?: string | number;
  align?: 'left' | 'center' | 'right';
  sortable?: boolean; // デフォルト true
  renderCell?: (row: T) => ReactNode;
  renderEditCell?: (row: T, onChange: (updated: T) => void) => ReactNode;
  hidden?: boolean;
  headerSx?: SxProps<Theme>;
  cellSx?: SxProps<Theme>;
}

// --- コンポーネント Props ---
interface SortableTableProps<T> {
  columns: SortableTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string | number;

  // ソート
  sortColumn?: string | null;
  sortDirection?: 'asc' | 'desc';
  onSort?: (columnKey: string) => void;

  // 行クリック
  onRowClick?: (row: T) => void;
  rowSx?: (row: T) => SxProps<Theme>;

  // インライン編集
  editingRowId?: string | number | null;
  onRowChange?: (updated: T) => void;

  // 行選択（チェックボックス）
  selectable?: boolean;
  selectedRowIds?: ReadonlySet<string | number>;
  onSelectedRowsChange?: (ids: ReadonlySet<string | number>) => void;

  // サマリー行
  summaryRow?: ReactNode;

  // テーブルスタイル
  size?: 'small' | 'medium';
  stickyHeader?: boolean;
  fixedLayout?: boolean;
  emptyMessage?: string;
}

export default function SortableTable<T>({
  columns,
  rows,
  rowKey,
  sortColumn,
  sortDirection = 'asc',
  onSort,
  onRowClick,
  rowSx,
  editingRowId,
  onRowChange,
  selectable,
  selectedRowIds,
  onSelectedRowsChange,
  summaryRow,
  size = 'small',
  stickyHeader,
  fixedLayout,
  emptyMessage = i18next.t('common.no_data'),
}: SortableTableProps<T>) {
  const visibleColumns = columns.filter(col => !col.hidden);

  // 全選択の状態計算
  const allSelected = selectable && rows.length > 0 && rows.every(r => selectedRowIds?.has(rowKey(r)));
  const someSelected = selectable && rows.some(r => selectedRowIds?.has(rowKey(r))) && !allSelected;

  const handleSelectAll = (checked: boolean) => {
    if (!onSelectedRowsChange) return;
    if (checked) {
      const all = new Set(rows.map(r => rowKey(r)));
      // 既存の選択を保持しつつ追加
      if (selectedRowIds) {
        selectedRowIds.forEach(id => all.add(id));
      }
      onSelectedRowsChange(all);
    } else {
      // 全解除
      onSelectedRowsChange(new Set());
    }
  };

  const handleSelectRow = (id: string | number) => {
    if (!onSelectedRowsChange || !selectedRowIds) return;
    const next = new Set(selectedRowIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    onSelectedRowsChange(next);
  };

  const renderSortIcon = (columnKey: string) => {
    if (sortColumn !== columnKey) {
      return <UnfoldMoreIcon sx={{ fontSize: '1rem', ml: 0.5, opacity: 0.3 }} />;
    }
    return sortDirection === 'asc'
      ? <ArrowUpwardIcon sx={{ fontSize: '1rem', ml: 0.5 }} />
      : <ArrowDownwardIcon sx={{ fontSize: '1rem', ml: 0.5 }} />;
  };

  const totalColSpan = visibleColumns.length + (selectable ? 1 : 0);

  return (
    <TableContainer>
      <Table size={size} stickyHeader={stickyHeader} sx={fixedLayout ? { tableLayout: 'fixed', width: '100%', '& th, & td': { boxSizing: 'border-box' } } : undefined}>
        <TableHead>
          <TableRow sx={{ bgcolor: '#f8f9fa' }}>
            {selectable && (
              <TableCell padding="checkbox" sx={{ bgcolor: '#f8f9fa', fontWeight: 600 }}>
                <Checkbox
                  size="small"
                  indeterminate={someSelected}
                  checked={allSelected || false}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </TableCell>
            )}
            {visibleColumns.map((col) => {
              const isSortable = col.sortable !== false && !!onSort;
              return (
                <TableCell
                  key={col.key}
                  align={col.align || 'center'}
                  sx={{
                    bgcolor: '#f8f9fa',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    px: '8px',
                    width: col.width,
                    minWidth: col.minWidth,
                    whiteSpace: 'nowrap',
                    ...(isSortable && {
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': { opacity: 0.7 },
                    }),
                    ...(col.headerSx as object),
                  }}
                  onClick={isSortable ? () => onSort!(col.key) : undefined}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: col.align || 'center' }}>
                    {col.label}
                    {isSortable && renderSortIcon(col.key)}
                  </Box>
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={totalColSpan} sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  {emptyMessage}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => {
              const id = rowKey(row);
              const isEditing = editingRowId != null && editingRowId === id;
              const isSelected = selectable && selectedRowIds?.has(id);
              const customRowSx = rowSx ? rowSx(row) : {};

              return (
                <TableRow
                  key={id}
                  hover
                  selected={isSelected}
                  sx={{
                    cursor: onRowClick ? 'pointer' : undefined,
                    '& .MuiTableCell-root': { py: '6px', px: '8px', fontSize: '0.8rem' },
                    ...(customRowSx as object),
                  }}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {selectable && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={isSelected || false}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => handleSelectRow(id)}
                      />
                    </TableCell>
                  )}
                  {visibleColumns.map((col) => {
                    const cellAlign = col.align || 'center';
                    const content = isEditing && col.renderEditCell
                      ? col.renderEditCell(row, (updated) => onRowChange?.(updated))
                      : col.renderCell
                        ? col.renderCell(row)
                        : (row as any)[col.key];

                    return (
                      <TableCell
                        key={col.key}
                        align={cellAlign}
                        sx={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          ...(col.cellSx as object),
                        }}
                      >
                        {content}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })
          )}
        </TableBody>
        {summaryRow && (
          <TableFooter>
            {summaryRow}
          </TableFooter>
        )}
      </Table>
    </TableContainer>
  );
}
